import React, { useContext } from 'react';
import { ProgressStyled } from './ProgressStyled';
import Check from '../Icons/fill/Check';
import { C2F_BREADCRUMBS, getBreadcrumbs, getCurrentDesktopStepIndex, STEP_INDEX } from '~/helpers/breadcrumbs';
import { ReduxState } from '~/types/redux';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import clientSide from '~/helpers/clientSide';
import { FuelContext } from '~/providers/FuelProvider';
import { PAGE_ROUTES } from '~/constants/pages';
import { setStepChangeTo } from '~/redux/actions/breadcrumbsControl';
import { trackEvent } from '~/helpers/tracking';
import { useChatbotContext } from '~/hooks/chatbot/useChatbotContext';

interface Props {
  c2fEnabled?: boolean;
}

const Progress: React.FC<Props> = React.memo(({ c2fEnabled }) => {
  const dispatch = useDispatch();
  const { accordionStep, salesJourney, quiz } = useSelector(
    (store: ReduxState) => ({
      accordionStep: store.breadcrumbsControl?.accordionStep,
      salesJourney: store.products?.salesJourney,
      quiz: store.quiz,
    }),
    shallowEqual
  );
  const { router } = useContext(FuelContext);
  const { chatType, showChat } = useChatbotContext();

  const isScheduleBeforeCredit = salesJourney === 'scheduleBeforeCredit';
  const breadcrumbs = c2fEnabled ? C2F_BREADCRUMBS : getBreadcrumbs({ isScheduleBeforeCredit });

  if (!clientSide) return null;

  const currentIndex = getCurrentDesktopStepIndex(router.route, isScheduleBeforeCredit, accordionStep, c2fEnabled);

  if (currentIndex === -1) return null;

  const getPlanPackagePage = () => {
    const isNextGenCart = quiz.isNextGenCart;

    if (isNextGenCart) {
      const nextGenCartPage = quiz.nextGenCartPage;
      if (nextGenCartPage === 'build') return PAGE_ROUTES.BUILD;
      if (nextGenCartPage === 'recommendation') return PAGE_ROUTES.RECOMMENDATION;
    }

    return PAGE_ROUTES.PLAN_PACKAGE;
  };

  const handleCompleteStepClick = async (stepName: string) => {
    trackEvent({
      action: 'elementClicked',
      data: {
        elementType: 'Progress Step',
        position: 'progress_step',
        location: 'progress_bar',
        text: stepName,
      },
    });

    const stepActions = {
      Plan: async () => {
        dispatch(setStepChangeTo(STEP_INDEX.BAU.PLAN));
        await router.push(getPlanPackagePage());
        if (chatType !== 'DISABLED') {
          showChat('back-button');
        }
      },
      Contact: async () => {
        dispatch(setStepChangeTo(0));
        await router.push(PAGE_ROUTES.CONTACT_CREDIT);
        if (chatType !== 'DISABLED') {
          showChat('back-button');
        }
      },
      Installation: async () => {
        if (isScheduleBeforeCredit) {
          dispatch(setStepChangeTo(STEP_INDEX.IBC.PLAN));
          await router.push(PAGE_ROUTES.INSTALLATION);
          if (chatType !== 'DISABLED') {
            showChat('back-button');
          }
        } else if (accordionStep > 1) {
          dispatch(setStepChangeTo(accordionStep - 1));
        }
      },
    };

    await stepActions[stepName as keyof typeof stepActions]?.();
  };

  return (
    <ProgressStyled>
      {breadcrumbs.map((step, index) => {
        const isLast = index === breadcrumbs.length - 1;
        const status = index < currentIndex ? 'complete' : index === currentIndex ? 'current' : 'upcoming';

        return (
          <div
            className={`step ${status}`}
            key={step.name}
            {...(status === 'complete' && {
              onClick: () => handleCompleteStepClick(step.name),
            })}
          >
            <div className={`circle ${status}`}>
              {status === 'complete' ? (
                <Check width="16" height="16" color="#7FFFD4" />
              ) : (
                <span className="step-number">{index + 1}</span>
              )}
            </div>
            {!isLast && <div className="step-line" />}
            <span className="step-name">{step.name}</span>
          </div>
        );
      })}
    </ProgressStyled>
  );
});

Progress.displayName = 'Progress';

export default Progress;
